import { Component, input } from '@angular/core'

@Component({
  selector: 'app-introduction',
  template: `
    <!-- Step 0 -->
    @if (!step()) {
    <div class="content is-normal pt-5">
      <h1 class="title is-2">Before you begin</h1>
      <p>Thank you for providing details on new or updated AI initiatives.</p>

      <h2 class="title is-5">
        Before continuing with your submission, you may wish to:
      </h2>
      <ul>
        <li>View sample initiatives from the OECD.AI Policy Navigator</li>
      </ul>

      <h2 class="title is-5">During the survey</h2>
      <p>
        Once you begin the submission process, you'll be asked to enter or
        review:
      </p>
      <ul>
        <li>
          Whether the initiative best fits one of the following categories:
          <ul>
            <li>National AI strategy</li>
            <li>AI governance bodies and mechanisms</li>
            <li>Regulations, guidelines and standards</li>
            <li>AI policy initiatives, programmes and projects</li>
          </ul>
        </li>

        <li>
          A description and details about the initiative, such as its name,
          status, budget (depending on the type of initiative), and the theme(s)
          it relates to, among others.
        </li>
        <li>Links, relevant files, and other supporting material(s).</li>
      </ul>

      <p>
        Please remember to save your submission from time to time as you
        progress through the survey.
      </p>
    </div>
    }

    <!-- Step 2 -->

    <!-- Step 3 -->
    @if (step() === 2) {
    <div class="content is-normal pt-5">
      <h1 class="title is-2">Initiative evaluation(s)</h1>

      <!-- Special case: Step 3 with no fields -->
      @if (isNotConcernedByEvaluation()) {
      <div class="columns">
        <div
          class="column is-10-tablet is-offset-1-tablet is-8-desktop is-offset-2-desktop is-6-fullhd is-offset-3-fullhd"
        >
          <div class="message is-primary">
            <div class="message-body">
              ℹ️ The OECD is not currently collecting Initiative Evaluation
              information for this type of initiative. You may proceed directly
              to the next step.
            </div>
          </div>
        </div>
      </div>
      } @else {
      <p>
        Evaluations involve the systematic and objective assessment of an
        ongoing or completed initiative, its design, implementation and results.
      </p>

      <p>
        Details about the extent to which AI initiatives have been, or will be,
        evaluated is of significant importance to researchers, as well as other
        governments who seek to learn and take inspiration from what has worked
        -- and what has not -- in other governments. Evaluations are also
        important tools for public transparency and accountability.
      </p>

      <p>
        Please add details related to evaluations that have been, or are planned
        to be, conducted with regards to the initiative. Such evaluations could
        be done in-house or independently (e.g., using a consulting firm, a
        national auditing institution, a bilateral aid agency (e.g., AFD, GIZ,
        IDRC, SIDA), an international organisation like the OECD, EU, IDB etc.).
      </p>
      }
    </div>
    }

    <!-- Step 4 -->
    @if (step() === 3) {
    <div class="content is-normal pt-5">
      <h1 class="title is-2">Links and relevant material(s)</h1>
      <p>
        On this page, you will be asked to provide URLs and other relevant
        materials, such as images, PDFs and other files, related to the
        initiative.
      </p>

      <p>
        These items are critical to help policymakers and researchers learn more
        about your AI initiative, and to help improve the accuracy of the
        OECD.AI Policy Observatory's AI-assisted tool,
        <a href="https://oecd.ai/ai-toolkit/" target="_blank"
          >AI Policy Toolkit</a
        >. Please do not upload any materials that you do not wish to be made
        public on OECD.AI.
      </p>
    </div>
    }
  `
})
export class IntroductionComponent {
  readonly step = input<number | null>(null)
  readonly isNotConcernedByEvaluation = input<boolean | null>(null)
}
